import { CircularProgress } from '@mui/material'
import { singin } from 'api'
import { Button } from 'components/buttons'
import { Icon } from 'components/icon'
import { Typography } from 'components/typography'
import { MenuContext } from 'context'
import { useToast } from 'hooks'
import {
	Icons,
	SingigResponse,
	SinginRequest,
	StepsLoginProps,
	TypographyTypes
} from 'interfaces'
import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Cookies from 'universal-cookie'
import { setSentryUser } from 'utils/sentry'
import { FormLogin } from './formLogin'
import { FormOtp } from './formOtp'

export const StepsLogin = ({
	setStep,
	setUser,
	user
}: StepsLoginProps): JSX.Element => {
	const [requestOtp, setRequestOtp] = useState(false)
	const [failedAttempts, setfailedAttempts] = useState<number>(0)

	const [loading, setLoading] = useState(false)
	const [bodyLogin, setBodyLogin] = useState<SinginRequest>()
	const { setFullName } = useContext(MenuContext)
	const navigate = useNavigate()
	const { showToast } = useToast()
	const cookies = new Cookies()
	const rememberName = cookies.get('remember_name')
	const [isRemember, setIsRemember] = useState(!!rememberName)

	const handleRememberMe = (signinUsername: string): void => {
		if (isRemember) {
			cookies.set('remember_name', signinUsername, { path: '/' })
		} else if (rememberName && !isRemember) {
			cookies.remove('remember_name', { path: '/' })
		}
	}

	const handleAdminLogin = (response: SingigResponse): void => {
		setFullName(response.full_name as string)
		const adminCookies = {
			fullName: response.full_name,
			userId: response.id,
			isSuperAdmin: response.is_community_admin,
			token: response.access_token
		}

		Object.entries(adminCookies).forEach(([key, value]) =>
			cookies.set(key, value, { path: '/' })
		)

		setSentryUser({
			id: response.id as number,
			userId: response.id as number,
			name: response.full_name as string
		})

		if (response.organizations?.length) {
			const org = response.organizations[0]
			const orgCookies = {
				organization_id: org.id,
				organization_name: org.name,
				organization_logo: org.icon_image_url,
				organization_slug: org.slug,
				roles: org.roles
			}

			Object.entries(orgCookies).forEach(([key, value]) =>
				cookies.set(key, value, { path: '/' })
			)
		}
	}

	const messageError = (type: string): string => {
		switch (type) {
			case 'Wrong code':
				return 'Invalid code, please try again in this device.'
			default:
				return type
		}
	}

	const handleUserLogin = (response: SingigResponse): void => {
		if (typeof response?.detail === 'object' && 'message' in response.detail) {
			setfailedAttempts(response.detail.failed_attempts)
			showToast(messageError(response.detail.message), '', 'error')
			return
		}
		if (!response.organizations) {
			showToast('The user does not have permissions', '', 'error')
			return
		}

		const hasPermissions = response.organizations.some(org =>
			[2, 3, 4, 5].some(role => org.roles.includes(role))
		)

		if (!hasPermissions) {
			showToast('The user does not have permissions', '', 'error')
			return
		}

		setUser({
			...user,
			email: response.email as string,
			fullName: response.full_name as string,
			userId: response.id as number,
			token: response.access_token as string
		})
		setStep(1)
	}

	const handleSingin = async (body: SinginRequest): Promise<void> => {
		try {
			setLoading(true)
			const response = await singin(body)

			setLoading(false)

			if (!response) return

			if (response.detail && typeof response?.detail === 'string') {
				showToast(response.detail, '', 'error')
				return
			}

			handleRememberMe(body.signin_username)

			if (response.is_community_admin) {
				handleAdminLogin(response)
				navigate('/superAdmin/organization')
				return
			}

			handleUserLogin(response)
		} catch (error) {
			setLoading(false)
			showToast('An error occurred during sign-in', '', 'error')
		}
	}

	return (
		<div>
			{loading ? (
				<div className="flex items-center justify-center w-full h-[400px] py-2 px-1 ">
					<CircularProgress disableShrink />
				</div>
			) : (
				<div>
					{failedAttempts === 3 ? (
						<div className="w-[386px]  h-[400px] bg-transparent-default p-6 rounded-[16px] flex flex-col justify-center items-center border-[1px] border-white">
							<Typography
								title="Hey!"
								fontLeading="12/12"
								type={TypographyTypes.p}
								className="text-white mb-6 !font-normal !text-base text-center"
							/>
							<Icon src={Icons.question} />
							<Typography
								title="You have reached the maximum number of attempts, please contact support for assistance."
								fontLeading="12/12"
								type={TypographyTypes.p}
								className="text-white mb-6 !font-normal !text-base text-center"
							/>
							<Button
								label="Ok"
								type="button"
								color="Transparent"
								onClick={() => {
									setRequestOtp(false)
									setfailedAttempts(0)
									setBodyLogin(undefined)
								}}
								className="!min-w-[unset] min-h-[54px] !w-[168px] !py-3 !px-6 !bg-white border-[1px] border-blue-primary border-solid mb-2 shadow-[0_4px_10px_rgba(255,255,255,0.5)]"
							/>
						</div>
					) : !requestOtp ? (
						<FormLogin
							setBodyLogin={setBodyLogin}
							setRequestOtp={setRequestOtp}
							setIsRemember={setIsRemember}
							setLoading={setLoading}
							singin={handleSingin}
							setfailedAttempts={setfailedAttempts}
						/>
					) : (
						<FormOtp
							bodyLogin={bodyLogin}
							singin={handleSingin}
							setLoading={setLoading}
						/>
					)}
				</div>
			)}
		</div>
	)
}
